import { faqsPl } from "@data/utrzymanie-aplikacji-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  maintenanceClusterLinks,
  maintenanceKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/maintenance-landing/shared";
import {
  Activity,
  AlertTriangle,
  Calendar,
  CheckCircle,
  Clock,
  Code,
  FileText,
  Globe,
  LifeBuoy,
  RefreshCw,
  Search,
  Server,
  Shield,
  Smartphone,
  Users,
  Wrench,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Utrzymanie aplikacji - SLA, monitoring i szybkie reagowanie na błędy";

export const description =
  "Utrzymanie aplikacji mobilnej lub webowej: SLA, monitoring 24/7 i szybkie reagowanie na błędy. Polski software house przejmuje opiekę nad istniejącym systemem — niezależnie od platformy.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Utrzymanie aplikacji mobilnych i webowych",
    description,
    "Application maintenance and SLA support",
  ),
  faqStructuredData(faqsPl),
];

export const heroCopy: HeroCopy = {
  header: "Utrzymanie",
  headerTail: "aplikacji",
  description:
    "Przejmujemy opiekę nad aplikacją mobilną lub webową: monitoring, SLA i szybkie reagowanie na błędy. Ten sam model opieki niezależnie od platformy — liczy się stabilność produkcji, nie stack marketingowy.",
  cta: {
    text: "Omów pakiet SLA",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Zobacz zakres",
    href: "#uslugi",
  },
  benefits: [
    { text: "Mobile i web — jedno SLA", Icon: CheckCircle },
    { text: "Monitoring i alerty", Icon: CheckCircle },
    { text: "Szybka reakcja na awarie", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo jest utrzymanie aplikacji?",
    p: "Ta strona jest dla zespołów, które mają już produkt na produkcji — nie budują MVP od zera. Szukacie partnera do SLA, monitoringu i poprawek, bo poprzedni dostawca zniknął, zespół in-house jest przeciążony albo awarie kosztują zbyt dużo. Brzmi znajomo?",
  },
  points: [
    {
      Icon: AlertTriangle,
      title: "Aplikacja pada, a nikt nie wie, kto ma reagować",
      description:
        "Brak dyżuru, brak alertów albo zgłoszenia giną w mailach. Potrzebujecie jasnego SLA: kto reaguje, w jakim czasie i jak raportuje.",
    },
    {
      Icon: Smartphone,
      title: "Macie appkę mobilną, web albo jedno i drugie",
      description:
        "Model utrzymania jest ten sam. Mobile i web różnią się narzędziami, nie obietnicą: monitoring, poprawki, release'y i rozwój w kontrolowanym tempie.",
    },
    {
      Icon: Users,
      title: "Poprzedni software house nie dowozi czasu reakcji",
      description:
        "Chcecie zmienić dostawcę utrzymania bez chaosu: przejęcie repo, CI/CD, krótkie mapowanie ryzyk i start opieki ze SLA.",
    },
    {
      Icon: RefreshCw,
      title: "Potrzebujecie aktualizacji store, SDK i zależności",
      description:
        "iOS/Android, przeglądarki, biblioteki, certyfikaty — utrzymanie obejmuje też „nudne, ale krytyczne” prace, które inaczej odkładają się na kwartał.",
    },
    {
      Icon: LifeBuoy,
      title: "Chcecie rozwoju funkcji bez utraty stabilności",
      description:
        "Retainer albo sprinty rozwojowe obok SLA: najpierw gasimy pożary, potem dokładamy funkcje w bezpiecznych release'ach.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opiszcie stack i jak dziś wygląda reagowanie na błędy — zaproponujemy pakiet utrzymania albo krótki przegląd przed SLA.",
    href: "#kontakt",
    buttonText: "Umów rozmowę o utrzymaniu",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto oddać nam utrzymanie aplikacji?",
    p: "Nie sprzedajemy „opieki w tle”. Budujemy przejrzysty model: monitoring, priorytety incydentów, czas reakcji i comiesięczny obraz zdrowia systemu — ten sam dla aplikacji mobilnej i webowej.",
    imageAlt: "Utrzymanie aplikacji — SLA, monitoring i reakcja na błędy",
  },
  values: [
    {
      Icon: Shield,
      title: "SLA dopasowane do ryzyka biznesowego",
      description:
        "Krytyczne awarie vs drobne UX — różne czasy reakcji. Nie obiecujemy 24/7 wszystkim, jeśli produkt tego nie wymaga.",
    },
    {
      Icon: Activity,
      title: "Monitoring zanim użytkownik napisze na support",
      description:
        "Alerty na dostępność, błędy i kluczowe ścieżki. Cel: wiedzieć o problemie wcześniej niż klienci.",
    },
    {
      Icon: Globe,
      title: "Jeden proces dla mobile i web",
      description:
        "Łączymy dawne ścieżki utrzymania app i systemów webowych: te same zasady SLA i raportowania, niezależnie od platformy.",
    },
    {
      Icon: Wrench,
      title: "Stabilizacja przed „big rewrite”",
      description:
        "Najpierw przywracamy przewidywalność produkcji. Modernizację legacy proponujemy, gdy widać realny zwrot — nie z automatu.",
    },
    {
      Icon: Clock,
      title: "Przejrzysty retainer i komunikacja",
      description:
        "Pakiet godzin, kolejka priorytetów, status incydentów. Wiecie, za co płacicie i co zostało zrobione w miesiącu.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Co obejmuje utrzymanie aplikacji mobilnej lub webowej",
    p: "Od przejęcia opieki po monitoring, hotfixy i kontrolowany rozwój. Zakres pakietu dopasowujemy do krytyczności systemu i Waszego zespołu.",
  },
  services: [
    {
      Icon: Search,
      title: "Przegląd przed startem SLA",
      description:
        "Dostęp do repo, CI/CD, hosting, historia awarii. Mapujemy ryzyka i proponujemy poziom opieki.",
    },
    {
      Icon: Activity,
      title: "Monitoring i alerty",
      description:
        "Obserwowalność błędów, uptime i krytyczne ścieżki — z alertami do zespołu dyżurnego.",
    },
    {
      Icon: AlertTriangle,
      title: "Reagowanie na incydenty",
      description:
        "Hotfixy, rollback, komunikacja statusu. Czas reakcji i naprawy zapisany w SLA.",
    },
    {
      Icon: Smartphone,
      title: "Utrzymanie aplikacji mobilnych",
      description:
        "Crashy, aktualizacje store, SDK, push, zgodność z nowymi wersjami iOS/Android.",
    },
    {
      Icon: Server,
      title: "Utrzymanie aplikacji webowych i API",
      description:
        "Backend, panel, integracje, wydajność i bezpieczeństwo zależności — pod tym samym SLA.",
    },
    {
      Icon: RefreshCw,
      title: "Aktualizacje i higiena techniczna",
      description:
        "Patchowanie bibliotek, certyfikaty, drobne refaktory blokujące bezpieczne release'y.",
    },
    {
      Icon: Code,
      title: "Rozwój w ramach retainera",
      description:
        "Nowe funkcje i usprawnienia w limicie godzin — bez mieszania ich z gaszeniem pożarów.",
    },
    {
      Icon: FileText,
      title: "Raportowanie i rekomendacje",
      description:
        "Miesięczny obraz: incydenty, zużycie godzin, ryzyka i propozycje kolejnych kroków (np. audyt lub modernizacja).",
    },
  ],
  cta: {
    h4: "Nie wiecie, jaki pakiet SLA wybrać?",
    p: "Opiszcie aplikację i jak dziś wyglądają awarie — zaproponujemy zakres monitoringu i czasu reakcji.",
    button: {
      text: "Umów rozmowę o utrzymaniu",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak przejmujemy utrzymanie aplikacji",
    p: "Od rozmowy po działające SLA. Przejrzyste etapy — bez wielomiesięcznego „discovery dla discovery”.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Rozmowa o systemie i oczekiwaniach",
      duration: "w kilka godzin",
      description:
        "Stack, użytkownicy, historia awarii, wymagany czas reakcji, budżet miesięczny.",
    },
    {
      Icon: Search,
      title: "Dostęp i krótki przegląd",
      duration: "w kilka dni",
      description:
        "Repo, CI/CD, monitoring (jeśli jest), środowiska. Lista ryzyk startowych i rekomendowany pakiet.",
    },
    {
      Icon: FileText,
      title: "Umowa SLA i model współpracy",
      duration: "ustalenie harmonogramu",
      description:
        "NDA, priorytety incydentów, kanały zgłoszeń, retainer, zasady release'ów.",
    },
    {
      Icon: Wrench,
      title: "Stabilizacja i handover operacyjny",
      duration: "1–3 tygodnie",
      description:
        "Alerty, runbooki, dostęp dyżurny, domknięcie najgroźniejszych dziur przed „biznes as usual”.",
    },
    {
      Icon: Activity,
      title: "Bieżące utrzymanie",
      duration: "na stałe",
      description:
        "Monitoring, poprawki, aktualizacje, komunikacja statusu według SLA.",
    },
    {
      Icon: Code,
      title: "Kontrolowany rozwój",
      duration: "wg planu",
      description:
        "Funkcje i usprawnienia w sprincie lub limicie godzin — osobno od kolejek awaryjnych.",
    },
    {
      Icon: FileText,
      title: "Przegląd miesięczny",
      duration: "co miesiąc",
      description:
        "Raport incydentów, zużycie pakietu, rekomendacje (utrzymanie / audyt / modernizacja).",
    },
    {
      Icon: CheckCircle,
      title: "Eskalacja do audytu lub modernizacji",
      duration: "gdy potrzeba",
      description:
        "Gdy dług techniczny blokuje stabilność — proponujemy audyt kodu albo plan modernizacji legacy.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów utrzymanie aplikacji",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  imageAlt: "Zespół Blues Brackets — utrzymanie aplikacji i SLA",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o utrzymanie aplikacji",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Chcesz spokojnie spać przy aplikacji na produkcji?",
    p: "Umów rozmowę — opiszcie mobile lub web, a zaproponujemy SLA, monitoring i model reakcji na błędy.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów rozmowę o utrzymaniu",
    p: "Utrzymanie aplikacji mobilnej lub webowej: SLA, monitoring i szybkie reagowanie na błędy. Polski software house.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      {
        href: "/utrzymanie-i-audyt-oprogramowania",
        text: "Utrzymanie i audyt oprogramowania",
      },
      ...maintenanceClusterLinks.filter(
        l =>
          l.href !== "/utrzymanie-i-audyt-oprogramowania" &&
          l.href !== "/utrzymanie-aplikacji",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...maintenanceKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
